//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

// #region Imports

import { Contract, EventLog, Signer } from 'ethers'

import { ActivityEnum, RoleEnum } from '@shared/Enums'

import { poolActions } from '@react/redux/poolSlice'

import { logOT } from '@shared/loggingShared'

import { ThunkAction, UnknownAction } from '@reduxjs/toolkit'
import { PoolFactoryAbi } from '@shared/Abis'

import { assertRole, dispatchError, OtStoreState } from '@react/redux/store'

import { navActions } from '@react/redux/navSlice'
import { otAssert } from '@shared/conversions'
import { UserConnection } from '@shared/DeploymentConfigTypes'
import { PerimeterChainAddressesInterface } from '@shared/jest.shared.helper'
import { ETH_ADDR_ZERO, EthAddr, TxHash } from '@shared/SchemaTypes'
import { txUUID, wsServerActionPoolTX, wsServerActionPoolTXRequest } from '@shared/WsServerActions'

export const createPoolTx =
  (props: {
    poolConfigSettings: string[]
    name: string
    tokenSymbol: string
    liquidityAssetAddress: EthAddr
  }): ThunkAction<void, OtStoreState, unknown, UnknownAction> =>
  async (dispatch, getState): Promise<void> => {
    const { poolConfigSettings, name, tokenSymbol, liquidityAssetAddress } = props
    const activity = ActivityEnum.POOL_CREATE
    try {
      logOT.info(activity, poolConfigSettings)

      const { webSocket } = getState().chainState
      const { chainConfigurationName, perimeterAddresses } = getState().chainState.chainConfiguration

      const { roleEnum, signer } = getState().chainState.userConnection as UserConnection
      if (roleEnum && perimeterAddresses && chainConfigurationName) {
        assertRole(roleEnum, RoleEnum.POOL_ADMIN)
        otAssert(!!signer, 'signer is not defined')

        const createPoolReturn = await createPool(
          perimeterAddresses,
          webSocket,
          signer as Signer,
          poolConfigSettings,
          name,
          tokenSymbol,
          liquidityAssetAddress,
          chainConfigurationName
        )
        logOT.info('createPoolReturn', createPoolReturn)
        const poolAddr: EthAddr = createPoolReturn.poolAddr
        dispatch(poolActions.creatPoolSuccess({ poolAddr }))

        dispatch(navActions.navigateTo({ url: `/pools/${poolAddr}` }))
      }
    } catch (e: any) {
      dispatch(poolActions.creatPoolFail())
      await dispatchError(dispatch, e, activity, 1047)
    }
  }

export const createPool = async (
  perimeterAddresses: PerimeterChainAddressesInterface,
  webSocket: WebSocket | undefined,
  poolAdminSigner: Signer,
  poolConfigSettings: string[],
  name: string,
  tokenSymbol: string,
  liquidityAssetAddress: EthAddr,
  chainConfigurationName: string
): Promise<{
  poolAddr: EthAddr
}> => {
  logOT.debug('call create Pool')

  logOT.debug('createPool poolConfigSettings', poolConfigSettings, perimeterAddresses.PoolFactory, !!poolAdminSigner)

  logOT.debug('PerimeterAddresses.PoolFactory,', perimeterAddresses.PoolFactory)

  const poolFactoryContract = new Contract(perimeterAddresses.PoolFactory, PoolFactoryAbi, poolAdminSigner)

  otAssert(poolConfigSettings.length === 6)
  otAssert(Number(poolConfigSettings[0]) > 1000000)
  otAssert(Number(poolConfigSettings[1]) > 2000000000)

  otAssert(poolConfigSettings[2].startsWith('0x'))
  otAssert(poolConfigSettings[3].startsWith('0x'))

  otAssert(Number(poolConfigSettings[5]) >= 0 && Number(poolConfigSettings[4]) < 3600 * 24)

  logOT.debug(
    'PerimeterAddresses.PoolFactory,',
    poolConfigSettings,
    await poolAdminSigner.getAddress(),

    name,
    tokenSymbol
  )

  logOT.debug('liquidityAssetAddress,', liquidityAssetAddress)

  logOT.debug('Create Pool', liquidityAssetAddress, poolConfigSettings, name, tokenSymbol)
  const uuid = txUUID()
  webSocket?.send(wsServerActionPoolTXRequest(ActivityEnum.POOL_CREATE, ETH_ADDR_ZERO, uuid, chainConfigurationName))

  const createdPoolTx = await poolFactoryContract.createPool(
    liquidityAssetAddress,
    poolConfigSettings,
    name,
    tokenSymbol
  )

  logOT.debug('PerimeterAddresses.createdPoolTx,', createdPoolTx)

  const receipt = await createdPoolTx.wait(1)

  const poolCreatedEvent = receipt.logs?.find((event: EventLog) => event.eventName === 'PoolCreated')

  logOT.debug(' poolCreatedEvent ', poolCreatedEvent)
  if (!poolCreatedEvent) {
    logOT.error('poolCreatedEvent not found', createdPoolTx)
  }

  const poolAddr = poolCreatedEvent.args.addr as EthAddr

  webSocket?.send(
    wsServerActionPoolTX(ActivityEnum.POOL_CREATE, poolAddr, uuid, createdPoolTx.hash as TxHash, chainConfigurationName)
  )

  logOT.debug('createPool poolAddr', poolAddr)

  return { poolAddr }
}
